/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import Glide from '@glidejs/glide';
import clsx from 'clsx';
import { CardCarouselContainerProps } from './LuxuryCarousel.types';
import { StyledCardCarouselContainer } from './LuxuryCarousel.styles';
import { respGridUtil } from '@marriott/mi-ui-library';
import { getDirection, shuffleCards, toggleArrowState } from '../../modules/helpers';

export const LuxuryCarousel: FC<CardCarouselContainerProps> = (props: CardCarouselContainerProps) => {
  const {
    cardCount,
    totalNumberOfCards,
    trackingProperties,
    styleclass,
    cqItems,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    enableTabletBreakpoint,
    cardId,
    previousLabel,
    nextLabel,
  } = props;

  const { noOfCards, noOfCardsTablet } = props;
  /**
   * return's the direction ('rtl' / 'ltr')...
   */
  const dir = getDirection();
  //returns the rtl/ltr
  const [direction, setDirection] = useState(false);
  const [cardsPerView, setCardsPerView] = useState(noOfCards);

  const handleResize = useCallback(() => {
    const xs = window.matchMedia('(max-width: 575px)');
    const sm = window.matchMedia('(min-width:576px) and (max-width: 767px)');
    const md = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
    const xl = window.matchMedia('(min-width: 1200px)');
    if (enableTabletBreakpoint) {
      if (xl?.matches) {
        setCardsPerView(noOfCards);
      } else if (md?.matches) {
        setCardsPerView(noOfCardsTablet);
      } else if (sm?.matches) {
        setCardsPerView(noOfCardsTablet);
      } else if (xs?.matches) {
        setCardsPerView(1);
      }
    } else {
      if (xs?.matches) {
        setCardsPerView(1);
      } else if (sm?.matches) {
        setCardsPerView(1);
      } else if (md?.matches) {
        setCardsPerView(noOfCardsTablet);
      } else if (xl?.matches) {
        setCardsPerView(noOfCards);
      }
    }
  }, []);

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    const updateStyleComponent = document.getElementById(`${componentId}__slides`);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode?.getAttribute('class');
          if (currWcmMode?.includes('Edit')) {
            updateStyleComponent?.classList.add('glide__slides_authoring');
          } else if (currWcmMode?.includes('Preview')) {
            updateStyleComponent?.classList.remove('glide__slides_authoring');
          }
        }
      });
    });
    observer.observe(pageWcmMode, { attributes: true });
    handleResize();

    window.addEventListener('resize', handleResize);

    setDirection(dir);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [cardId]);

  useEffect(() => {
    const carouselRootSelector = `#${componentId}`;
    if (document?.querySelector(carouselRootSelector)) {
      try {
        new Glide(carouselRootSelector, {
          type: 'slider',
          bound: true,
          perView: noOfCards,
          startAt: 0,
          rewind: false,
          gap: 16,
          dragThreshold: false,
          direction: dir ? 'rtl' : 'ltr',
          breakpoints: enableTabletBreakpoint
            ? {
                1199: {
                  perView: noOfCardsTablet,
                },
                575: {
                  perView: 1,
                },
              }
            : {
                1199: {
                  perView: noOfCardsTablet,
                },
                767: {
                  perView: 1,
                },
              },
        }).mount({ ArrowDisabler });

        const addArrowEventListener = (arrowId: string, clickTrack: string) => {
          const arrow = document?.querySelector(`#${arrowId}`);
          if (arrow) {
            arrow.addEventListener('touchstart', (e: any) => {
              document.getElementById(clickTrack)?.click();
              e.preventDefault();
              e.stopImmediatePropagation();
            });
            arrow.addEventListener('click', (e: any) => {
              document.getElementById(clickTrack)?.click();
              e.stopImmediatePropagation();
            });
          }
        };

        addArrowEventListener(`${componentId}_left_arrow`, 'left-arrow-click-track');
        addArrowEventListener(`${componentId}_right_arrow`, 'right-arrow-click-track');
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorMode, componentId, cardsPerView]);

  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }

            // Update arrow states
            const leftArrow = controlItem?.querySelector('.glide__arrow--left');
            const rightArrow = controlItem?.querySelector('.glide__arrow--right');

            // Disable left arrow on the first slide
            toggleArrowState(leftArrow, Glide.index === 0);

            // Disable right arrow on the last slide
            const isLastSlide = Glide.index === Components.Sizes.length - Glide.settings.perView;
            toggleArrowState(rightArrow, isLastSlide);
          }
        });
      },
    };
  };

  const customAttributes = trackingProperties?.enableScrollingBehavior ? { 'data-section-tracking': componentId } : {};

  // This function is used to render the component in authoring mode authorCardWrapper
  const authorCardWrapper = (index: number, innerCompStyleClass: string | undefined) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        columnCount="12"
        gridClassNames={''}
        customClassName={clsx(innerCompStyleClass)}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
      />
    );
  };

  /**
   * Renders the component in View Publish Mode ie., for end user
   * @param cardData | card's data
   * @returns | the card's in card carousel
   */
  const publishCardWrapper = (cardData: any, index: number) => {
    const data = props?.isRandom ? cardData : cqItems[cardData];
    return <ResponsiveGrid model={respGridUtil(data)} {...respGridUtil(data)} key={index} />;
  };

  return (
    <StyledCardCarouselContainer
      data-component-name="mi-luxury-CardCarouselContainer"
      data-testid="luxury-CardCarouselContainer"
      className={clsx('col-12 pb-3 pt-0 px-0', styleclass)}
      {...customAttributes}
    >
      <div id={componentId} className={clsx(`glide ${''}`)} key={cardId}>
        <div
          className={clsx('glide__track', totalNumberOfCards.length < 3 ? 'center__align__track' : '')}
          data-glide-el="track"
        >
          <ul className={clsx('glide__slides pb-3')} id={`${componentId}__slides`}>
            {isAuthorMode &&
              Array.from({ length: totalNumberOfCards.length }, (_, i) => authorCardWrapper(i, styleclass))}
            {!isAuthorMode &&
              (props?.isRandom
                ? shuffleCards(cqItems).map((cardData: object, i: number) => publishCardWrapper(cardData, i))
                : totalNumberOfCards.map((cardName: string, i: number) => publishCardWrapper(cardName, i)))}
          </ul>
        </div>
        {cardCount !== cardsPerView && (
          <div className="center-align">
            <div className="carouselControlType2">
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={clsx('left-arrow glide__arrow--left')}
                  id={`${componentId}_left_arrow`}
                  data-glide-dir={direction ? '>' : '<'}
                  aria-label={previousLabel}
                  data-content={previousLabel}
                >
                  <span
                    className={clsx(direction ? 'icon-arrow-right' : 'icon-arrow-left')}
                    id={`${componentId}_left_arrow`}
                    data-id="left"
                  ></span>
                </button>
              </div>
              <input
                type="hidden"
                id="left-arrow-click-track"
                className={clsx(`${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
                data-custom_click_track_value={`${trackingProperties?.cardLocation}|${trackingProperties?.leftArrowDesc}|internal`}
              />
              <div data-glide-el="controls[nav]">
                {Array.from({ length: cardCount - (cardsPerView - 1) }, (_, i) => (
                  <button className="" data-glide-dir={i} aria-label={`page-${i + 1}`} key={cardsPerView + i}></button>
                ))}
              </div>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={clsx('right-arrow glide__arrow--right')}
                  id={`${componentId}_right_arrow`}
                  data-glide-dir={direction ? '<' : '>'}
                  aria-label={nextLabel}
                  data-content={nextLabel}
                >
                  <span className={clsx(direction ? 'icon-arrow-left' : 'icon-arrow-right')} data-id="right"></span>
                </button>
              </div>
              <input
                type="hidden"
                id="right-arrow-click-track"
                className={clsx(`${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
                data-custom_click_track_value={`${trackingProperties?.cardLocation}|${trackingProperties?.rightArrowDesc}|internal`}
              />
            </div>
          </div>
        )}
      </div>
    </StyledCardCarouselContainer>
  );
};
